import { InfoCircleOutlined } from "@ant-design/icons"
import { Flex, Tabs, type TabsProps, Tooltip } from "antd"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { type BlockerFunction } from "react-router-dom"

import { FixedContainer } from "../../../../components/FixedContainer.tsx"
import ReactRouterPrompt from "../../../../components/ReactRouterPrompt.tsx"
import { type RootState } from "../../../../store.ts"
import { useReadsMessages } from "../../../Communication/timeline/RecordCommunicationTimeline.tsx"
import { WorkspaceUnsavedChangesModal } from "../cards/WorkspaceUnsavedChangesModal.tsx"
import {
  WorkspaceEditGuardProvider,
  useWorkspaceEditGuard
} from "../cards/workspace-edit-guard.tsx"
import {
  LEAD_WORKSPACE_TABS,
  type LeadWorkspaceTabContext
} from "../lead-workspace-tabs.tsx"
import { LeadMissingFieldsProvider } from "../missing/lead-missing-fields-context.tsx"
import { useLeadMissingFields } from "../missing/use-lead-missing-fields.ts"
import { useLeadWorkspaceTab } from "../use-lead-workspace-tab.ts"
import { LeadWorkspaceHeader } from "./LeadWorkspaceHeader/LeadWorkspaceHeader.tsx"
import { NeedsInfoEditorProvider } from "./LeadWorkspaceHeader/needs-info-editor-context.tsx"

type LeadWorkspaceProps = Omit<LeadWorkspaceTabContext, "onTabChange"> & {
  onStageRetry: () => void
}

function LeadWorkspaceBody({ onStageRetry, ...props }: LeadWorkspaceProps) {
  const { t } = useTranslation()
  const headerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(0)

  // The header and tab bar stick as one block by measuring the header's own
  // rendered height and pinning the tab bar right below it — rather than a
  // guessed pixel offset, which would drift the moment the header's content
  // (an extra badge, a wrapped line) changed its real height.
  useEffect(() => {
    const el = headerRef.current
    if (!el) return

    const observer = new ResizeObserver(([entry]) =>
      setHeaderHeight(entry.contentRect.height)
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const renderStickyTabBar: TabsProps["renderTabBar"] = (
    tabBarProps,
    DefaultTabBar
  ) => (
    <DefaultTabBar
      {...tabBarProps}
      className={"sticky z-10 bg-background"}
      style={{
        top: `calc(${headerHeight}px - var(--fixed-container-pad-top, 0px))`
      }}
    />
  )
  // `isVisible` only ever sees the lead — the Project Education tab's gate
  // is the signed-in user's own company feature flag, so it is filtered here
  // instead, alongside the per-lead `isVisible` checks.
  const hasEducationFeature = useSelector(
    (state: RootState) => !!state.auth.userInfo?.feature_project_education
  )
  const readsMessages = useReadsMessages()
  const visibleTabs = useMemo(
    () =>
      LEAD_WORKSPACE_TABS.filter((tab) => {
        if (tab.key === "education" && !hasEducationFeature) return false
        if (tab.key === "communication" && !readsMessages) return false
        return !tab.isVisible || tab.isVisible(props.lead)
      }),
    [props.lead, hasEducationFeature, readsMessages]
  )
  const { activeKey, setActiveKey } = useLeadWorkspaceTab(visibleTabs)
  const { hasUnsavedChanges, discardAll } = useWorkspaceEditGuard()
  const [pendingTabKey, setPendingTabKey] = useState<string | null>(null)
  const {
    missing,
    missingParam,
    pendingStatus,
    updatedInPlace,
    clearPendingStage
  } = useLeadMissingFields()

  // Once per deep link, not once per render: forcing the tab back after every
  // refetch would undo the user's own navigation while they answer the fields.
  // A retry rewrites the same params in place, which re-derives the marks but
  // must not move a user who is mid-edit — only an arrival opens a tab.
  const openedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!missingParam || !missing.firstTab) return
    if (openedFor.current === missingParam) return
    openedFor.current = missingParam
    if (updatedInPlace) return
    setActiveKey(missing.firstTab)
  }, [missingParam, missing.firstTab, updatedInPlace, setActiveKey])

  const changeTab = (key: string) => {
    if (hasUnsavedChanges) {
      setPendingTabKey(key)
      return
    }
    setActiveKey(key)
  }

  const context: LeadWorkspaceTabContext = { ...props, onTabChange: changeTab }

  // A tab switch only rewrites `?tab=`, so the router blocker — which answers
  // to the pathname — never sees it; the tab guard above is its counterpart.
  const blockNavigation: BlockerFunction = (args) => {
    if (!hasUnsavedChanges) return false
    if (!args?.nextLocation) return true
    return args.currentLocation.pathname !== args.nextLocation.pathname
  }

  return (
    <LeadMissingFieldsProvider missing={missing}>
      <FixedContainer>
        <div
          ref={headerRef}
          className={
            "sticky top-[calc(-1*var(--fixed-container-pad-top,0px))] z-20 bg-background"
          }
        >
          <LeadWorkspaceHeader
            lead={context.lead}
            filterOptions={context.filterOptions}
            canEdit={context.canEdit}
            canAssignOwner={context.canAssignOwner}
            saving={context.saving}
            pendingStatus={pendingStatus}
            onLeadSelect={context.onLeadSelect}
            onStageChange={context.onStageChange}
            onStageRetry={onStageRetry}
            onDismissPendingStage={clearPendingStage}
            onPatch={context.onPatch}
            onToggleFavorite={context.onToggleFavorite}
          />
        </div>

        <Tabs
          activeKey={activeKey}
          onChange={changeTab}
          renderTabBar={renderStickyTabBar}
          items={visibleTabs.map((tab) => {
            const count = missing.countByTab[tab.key] ?? 0
            return {
              key: tab.key,
              label: (
                <Flex align={"center"} gap={6}>
                  {t(tab.labelKey)}
                  {/* Hover-only, inside the same clickable tab button on
                      purpose — it must never swallow the tab's own click. */}
                  <Tooltip title={t(tab.descriptionKey)}>
                    <InfoCircleOutlined
                      className={"text-xs text-gray-400"}
                      aria-hidden
                    />
                  </Tooltip>
                  {count > 0 && (
                    <span
                      className={"error-count"}
                      aria-label={t("tabbedForm.errorBadge", { count })}
                    >
                      {count}
                    </span>
                  )}
                </Flex>
              ),
              children: tab.render(context)
            }
          })}
        />

        {pendingTabKey != null && (
          <WorkspaceUnsavedChangesModal
            open
            onStay={() => setPendingTabKey(null)}
            onDiscard={() => {
              discardAll()
              setActiveKey(pendingTabKey)
              setPendingTabKey(null)
            }}
          />
        )}

        <ReactRouterPrompt when={blockNavigation}>
          {({ isActive, onConfirm, onCancel }) => (
            <WorkspaceUnsavedChangesModal
              open={isActive}
              onStay={onCancel}
              onDiscard={() => {
                discardAll()
                onConfirm()
              }}
            />
          )}
        </ReactRouterPrompt>
      </FixedContainer>
    </LeadMissingFieldsProvider>
  )
}

export function LeadWorkspace(props: LeadWorkspaceProps) {
  return (
    <WorkspaceEditGuardProvider>
      <NeedsInfoEditorProvider>
        <LeadWorkspaceBody {...props} />
      </NeedsInfoEditorProvider>
    </WorkspaceEditGuardProvider>
  )
}
